import React, { useEffect, useRef, useState } from 'react';
import BottomSheet from '../../../components/ui/BottomSheet';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import PlannedPill from './PlannedPill';
import Switch from '../../../components/ui/Switch';
import Dropdown from '../../../components/ui/Dropdown';
import SectionToggle from './SectionToggle';
import NightTrainSearchButton from './NightTrainSearchButton';
import { getTransportEmoji, getModeLabel, getEmissionLevel, EMISSION_LEVEL_COLOR, EMISSION_LEVEL_LABEL, NIGHT_TRAIN_EMISSIONS_NOTE, isKnownTransportMode, isNightTrainMode, TRANSPORT_MODE_OPTIONS, FLIGHT_TIME_TOOLTIP } from '../../../shared/transport';
import NightTrainSheetBlock from './NightTrainSheetBlock';
import { NEARBY_SEARCH_LABEL, NEARBY_SEARCH_OTHER_LABEL, NightTrainStatus, bookingLabel, canSearchNearby, fmtRide, modeOptionsForLeg, searchBlockedReason } from '../nightTrain';
import { previewLegMode, previewLegPrice, compareLegModes, compareLegPrices, fetchLegOmioLink, fetchNightTrainBookingLink, CompareModeRow, LegUserOverrides, LegPriceEstimates, LegTicketDetails, LegAirportPair, LegNightTrain, NightTrainSearchSummary, OmioRoute } from '../../journeys/api';
import { analytics } from '../../../shared/analytics';
import { Dots } from './RouteTimeline';
import CompareTransportDialog, { CompareBand } from './CompareTransportDialog';
import useViewportWidth from '../../../hooks/useViewportWidth';
import Tooltip from '../../../components/ui/Tooltip';
import { coerceDecimal, formatDurationMin, formatDurationExact, formatEuroExact, parseDurationInput, parseEuroInput, overridesKey } from '../utils';

// Leg detail sheet (prototype #legSheet, handover §6), CORE slice: hero,
// "How you'll travel" (the shared transport dropdown), the booking button,
// and the sections (night train, your ticket, details, airports), which are
// all collapsible and closed by default (Paulo 2026-09-28). Everything edits
// a DRAFT that only commits on Save (edit-only, no delete; legs are
// a projection of stops). Picking a different mode LIVE-previews its facts so
// modes can be compared before saving: time/emissions come from the backend
// engine (read-only preview endpoint), the price band from an LLM estimate.
// Previews are cached per mode for the sheet session; nothing persists until
// Save. The Details rows for Travel time and Ticket price are USER-EDITABLE
// (per-mode overrides, schema doc `userOverrides`): tap to enter your own
// value, "Use estimate" to go back; overridden values render solid with a
// pencil glyph, estimates keep the dashed underline + asterisk. Emissions are
// never editable (tap explains why). The booking button (the Omio affiliate
// CTA, docs/integrations/omio-impact.md, or a night train's own channel,
// docs/product/night-trains/README.md) shows on unbooked legs, to everyone:
// owners, guests, and visitors of a shared trip (through `bookingLinks`).

/** One end of a flight leg's airport pair (backend: journeyTransform). */
export interface LegAirport {
  iata: string | null;
  name: string;
  city: string | null;
  /** Short label for the row; `fullName` is the official name (tooltip). */
  fullName?: string | null;
  /** Straight-line km from the city centre. Approximate, under-states road. */
  distanceFromCityKm: number;
}

export interface LegSheetLeg {
  id: string;
  transportMode?: string | null;
  /** Flight legs only: the airport pair the distance estimate is based on. */
  airports?: LegAirportPair | null;
  durationMinutes?: number | string | null;
  durationHours?: number | string | null;
  priceBandLowCents?: number | string | null;
  priceBandHighCents?: number | string | null;
  co2eKg?: number | string | null;
  planned?: boolean;
  userOverrides?: LegUserOverrides | null;
  priceEstimates?: LegPriceEstimates | null;
  ticketDetails?: LegTicketDetails | null;
  /** Night-train legs: the train (docs/product/night-trains/README.md). */
  nightTrain?: LegNightTrain | null;
  /** The timetable data has a night train for this leg. */
  nightTrainAvailable?: boolean;
  /** Why not, when it has none (the picker shows the mode disabled, with the reason). */
  nightTrainStatus?: NightTrainStatus | null;
  nightTrainNote?: string | null;
  /** What "Search night trains" would find: where it finds nothing the sheet says so, and does not offer it. */
  nightTrainSearch?: NightTrainSearchSummary | null;
}

export interface LegSheetProps {
  journeyId: string | null;
  /** The leg being viewed (live from journey state), or null when closed. */
  leg: LegSheetLeg | null;
  open: boolean;
  from: string;
  to: string;
  busy?: boolean;
  /** Expected travel day for this leg (YYYY-MM-DD) from the journey schedule
   *  walk; shown as the Date row's estimate until the user sets their own. */
  expectedDate?: string | null;
  /** Persist the drafted changes. Only changed fields are present. Resolve true on success. */
  onSave: (legId: string, changes: { transportMode?: string; planned?: boolean; plannedVia?: 'sheet' | 'save_dialog'; userOverrides?: LegUserOverrides | null; ticketDetails?: LegTicketDetails | null }) => Promise<boolean>;
  onClose: () => void;
  /** Night train that does not run on the leg's date: move a night between
   *  the two stops so it does (one tap, saved at once; spec §7.2). Absent on
   *  read-only views. Resolve true on success. */
  onShiftNightTrainDate?: (legId: string, date: string) => Promise<boolean>;
  /** Open "Search night trains nearby" for this leg (the action at the foot
   *  of the transport dropdown). Absent on read-only views. */
  onSearchNightTrainsNearby?: (legId: string) => void;
  /** The leg starts or ends at a change (a stop with 0 nights): the
   *  night-train block says "Change in …" (spec §7.2). */
  changeAtFrom?: boolean;
  changeAtTo?: boolean;
  /** Where the booking buttons get their links when the sheet has no
   *  journeyId, i.e. on the read-only shared view (its public, share-token
   *  endpoints). With a journeyId the sheet uses the journey's own routes. */
  bookingLinks?: LegBookingLinks;
  /** The leg's two stops (city + country), for the Omio link: its position
   *  lookup in Omio's suggester runs at the click (journeys/omioPositions.ts). */
  route?: OmioRoute | null;
}

export interface LegBookingContext { source: 'leg_sheet'; date: string | null; daysUntilDeparture: number | null; route?: OmioRoute | null }
export interface LegBookingLinks {
  omio: (legId: string, ctx: LegBookingContext) => Promise<{ url: string }>;
  nightTrain: (legId: string, ctx: LegBookingContext) => Promise<{ url: string }>;
}

// Canonical string for the ticket-details dirty check (order-stable).
const ticketKey = (t?: LegTicketDetails | null): string =>
  JSON.stringify({ date: t?.date ?? null, time: t?.time ?? null, extraInfos: (t?.extraInfos ?? '').trim() || null });

const TICKET_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// "2026-08-16" → "16 Aug 2026" (day first — Paulo 2026-07-18).
function fmtIsoDay(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${TICKET_MONTHS[m - 1]} ${y}`;
}

// Value parsing/formatting for user-entered costs & times lives in ../utils
// (parseEuroInput, parseDurationInput, formatDurationExact, formatEuroExact,
// overridesKey) — shared with CompareTransportDialog.

// Airports shown per city before the rest collapse (Paulo 2026-07-30). London
// has six in range; two keeps the booking CTA on screen even on a phone.
const AIRPORTS_SHOWN = 2;

interface ModePreview {
  facts?: { durationMinutes: number | null; co2eKg: number | null };
  /** Flight legs: airport pair for THIS mode, so the row appears on switch. */
  airports?: LegAirportPair | null;
  factsFailed?: boolean;
  band?: { lowCents: number; highCents: number };
  bandFailed?: boolean;
}

const sheetH: React.CSSProperties = { fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: 15, color: '#16241d', margin: 0 };
const fieldRow: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '11px 14px', borderBottom: '1px solid rgba(20,40,30,0.06)' };
const rowLab: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#6c7872', flex: 'none' };
const rowVal: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: '#16241d' };

// Leg CTAs. Radius 12 (not a 999 pill) on purpose: the Planned/Unplanned
// STATUS pills are fully round, so keeping actions square-ish separates "click
// me" from "this is the state". Tiers mirror the sheet's own footer: solid
// green = primary (Save), pale grey-green = secondary (Discard).
const ctaBase: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, flex: 'none',
  font: 'inherit', fontSize: 11.5, fontWeight: 700, borderRadius: 12, padding: '8px 12px', cursor: 'pointer',
};
const ctaPrimary: React.CSSProperties = { ...ctaBase, background: '#157a55', color: '#fff', border: 'none' };
const ctaSecondary: React.CSSProperties = { ...ctaBase, background: '#f4f6f3', color: '#6c7872', border: '1px solid rgba(20,40,30,0.06)' };

const bandStr = (lowCents: number, highCents: number): string => {
  const lo = Math.round(lowCents / 100);
  const hi = Math.round(highCents / 100);
  return lo === hi ? `€${lo}` : `€${lo}–${hi}`;
};

// Leaves-the-app marker for the Omio CTA. SVG line icon per the icon
// convention in docs/codebase/ui-conventions.md (never emoji for controls).
const ExternalGlyph: React.FC = () => (
  <svg width={11} height={11} viewBox="0 0 24 24" fill="none" aria-hidden style={{ flex: 'none' }}>
    <path d="M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PencilGlyph: React.FC = () => (
  <svg width={11} height={11} viewBox="0 0 24 24" fill="none" aria-hidden style={{ flex: 'none' }}>
    <path d="M14.5 5.5l4 4M3 21l4-1L20 7a2 2 0 0 0-3-3L4 17l-1 4z" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Tap-to-edit value for a Details row (user-editable costs & times). Estimate:
// dashed underline (prototype .frow-edit affordance). Overridden: solid value +
// pencil glyph. While editing: small input (Enter/blur commits, Escape cancels)
// plus "Use estimate (…)" to drop the override.
const EditableRowValue: React.FC<{
  display: string;
  overridden: boolean;
  estimateLabel: string;
  placeholder: string;
  ariaLabel: string;
  disabled?: boolean;
  muted?: boolean;
  /** Shown in red under the input when onCommit rejects the text — the red
   *  border alone read as "nothing happened" (Paulo 2026-08-18). */
  invalidHint?: string;
  onCommit: (raw: string) => boolean;
  onReset: () => void;
}> = ({ display, overridden, estimateLabel, placeholder, ariaLabel, disabled, muted, invalidHint, onCommit, onReset }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [invalid, setInvalid] = useState(false);

  const commit = () => {
    const raw = val.trim();
    if (!raw) { setEditing(false); setInvalid(false); return; } // empty = no change
    if (onCommit(raw)) { setEditing(false); setInvalid(false); } else { setInvalid(true); }
  };

  if (editing) {
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {overridden && (
          <button
            type="button"
            onClick={() => { setEditing(false); setInvalid(false); onReset(); }}
            style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 11.5, fontWeight: 600, color: '#157a55', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Use estimate ({estimateLabel})
          </button>
        )}
        <input
          autoFocus
          value={val}
          placeholder={placeholder}
          aria-label={ariaLabel}
          onChange={(e) => { setVal(e.target.value); setInvalid(false); }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { e.stopPropagation(); setEditing(false); setInvalid(false); }
          }}
          style={{
            width: 84, font: 'inherit', fontSize: 13.5, fontWeight: 700, color: '#16241d', textAlign: 'right',
            padding: '4px 8px', borderRadius: 8, outline: 'none',
            border: `1.5px solid ${invalid ? '#cf5a3c' : '#157a55'}`,
          }}
        />
      </span>
      {invalid && (
        <span style={{ fontSize: 11, fontWeight: 600, color: '#cf5a3c', whiteSpace: 'nowrap' }}>
          {invalidHint || 'Enter a number.'}
        </span>
      )}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => { if (!disabled) { setVal(''); setEditing(true); } }}
      aria-label={`Edit ${ariaLabel}`}
      title={overridden ? 'Your value. Tap to change it.' : 'Estimated. Tap to enter your own.'}
      style={{
        background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 14, fontWeight: overridden ? 700 : (muted ? 500 : 700),
        color: overridden ? '#16241d' : (muted ? '#6c7872' : '#16241d'), cursor: disabled ? 'default' : 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 5,
        borderBottom: overridden ? 'none' : '1.5px dashed #b3bbb6', paddingBottom: overridden ? 0 : 1,
      }}
    >
      {overridden && <span style={{ color: '#157a55', display: 'inline-flex' }}><PencilGlyph /></span>}
      {display}
    </button>
  );
};

// Transport modes Omio sells tickets for (train, bus, flight, ferry) — the
// only modes that get the "Find tickets on Omio" CTA. Mirrors the backend's
// omioAffiliateService.OMIO_MODES.
const OMIO_MODES = new Set(['train', 'bus', 'airplane', 'boat']);

const LegSheet: React.FC<LegSheetProps> = ({ journeyId, leg, open, from, to, route, busy, expectedDate, onSave, onClose, onShiftNightTrainDate, bookingLinks, changeAtFrom, changeAtTo, onSearchNightTrainsNearby }) => {
  const [shiftBusy, setShiftBusy] = useState(false);
  // Draft state: committed only on Save (prototype's legDraft pattern).
  const [mode, setMode] = useState('train');
  const [planned, setPlanned] = useState(false);
  // Per-mode user cost/time overrides draft (userOverrides). Seeded from the
  // leg on open; edited by the Details rows AND the compare dialog; saved as a
  // full-map replace. `initialOverridesRef` powers the dirty check.
  const [overrides, setOverrides] = useState<LegUserOverrides>({});
  const initialOverridesRef = useRef<string>('{}');
  const [saving, setSaving] = useState(false);
  // Ticket details draft (Tickets section): date/time/extra infos, committed
  // with the sheet's Save like everything else.
  const [ticket, setTicket] = useState<LegTicketDetails>({});
  const initialTicketRef = useRef<string>(ticketKey(null));
  // 'datetime' edits date + time together (one merged row, 2026-07-30).
  const [ticketEditing, setTicketEditing] = useState<null | 'datetime' | 'extra'>(null);
  // "Mark as planned?" prompt when saving a date on an Unplanned leg.
  const [askPlanned, setAskPlanned] = useState(false);
  // Guard against losing draft edits: any close attempt (backdrop tap, Escape,
  // the X, the Discard button) while dirty asks for confirmation first.
  const [askDiscard, setAskDiscard] = useState(false);
  // Transient "why can't I edit this" hint for the read-only Emissions row.
  const [emiHint, setEmiHint] = useState(false);
  const emiHintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showEmiHint = () => {
    setEmiHint(true);
    if (emiHintTimer.current) clearTimeout(emiHintTimer.current);
    emiHintTimer.current = setTimeout(() => setEmiHint(false), 4000);
  };
  // Per-mode preview cache for this sheet session (cleared when the sheet
  // (re)opens), so tapping back and forth between chips doesn't refetch or
  // re-call the LLM. Missing entry = not requested yet.
  const [previews, setPreviews] = useState<Record<string, ModePreview>>({});
  // Per-city expand state for the Airports block (keyed by city name).
  const [airportsExpanded, setAirportsExpanded] = useState<Record<string, boolean>>({});
  // Section collapse state. Every section starts CLOSED (Paulo 2026-07-30,
  // and "Your ticket" and the night train too since 2026-09-28): the sheet's
  // job on open is the transport picker and the booking button, which stay
  // in view; the rest is detail you go looking for.
  type SectionKey = 'nightTrain' | 'ticket' | 'details' | 'airports';
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({ nightTrain: false, ticket: false, details: false, airports: false });
  const toggleSection = (k: SectionKey) => setOpenSections((o) => ({ ...o, [k]: !o[k] }));
  const requestedRef = useRef<Set<string>>(new Set());
  const legIdRef = useRef<string | null>(null);
  // Responsive label for the compare pill (spec §2): full text from 390px up.
  const compactLabel = useViewportWidth() < 390;

  // Compare-transport-options data (prototype cmpx). The deterministic facts
  // table is fetched as soon as the sheet OPENS (no LLM): it feeds the per-mode
  // preview cache AND marks unavailable modes on the chips (airplane too short,
  // bicycle too long). The batched LLM price call only fires when the compare
  // dialog is actually opened. Both cached for the sheet session.
  const [cmpOpen, setCmpOpen] = useState(false);
  const [cmpRows, setCmpRows] = useState<CompareModeRow[] | null>(null);
  const [cmpMeta, setCmpMeta] = useState<{ from: string; to: string; distanceKm: number | null }>({ from: '', to: '', distanceKm: null });
  const [cmpError, setCmpError] = useState(false);
  const [cmpPrices, setCmpPrices] = useState<Record<string, CompareBand | undefined>>({});
  const [cmpPricesLoading, setCmpPricesLoading] = useState(false);
  const factsPromiseRef = useRef<Promise<Awaited<ReturnType<typeof compareLegModes>> | null> | null>(null);
  const pricesFetchedRef = useRef(false);

  // Omio affiliate CTA (Tickets section). Only for modes Omio sells tickets
  // for; the link itself is route-based (Omio's mode-agnostic search), so a
  // drafted mode switch between covered modes needs no new link.
  const [omioBusy, setOmioBusy] = useState(false);
  const [omioError, setOmioError] = useState(false);
  // Both booking buttons: the tab opens synchronously (before the await) so
  // popup blockers allow it. affiliate_link_clicked fires server-side on the
  // journey's own routes; the shared view's fetchers track it themselves.
  // The travel day (the user's ticket date wins over the schedule's expected
  // day, same precedence the ticket rows show) rides into the Omio deep link
  // as `date`, and doubles as event context via daysUntilDeparture.
  const canBook = Boolean(journeyId || bookingLinks);
  const openBookingLink = (kind: 'omio' | 'nightTrain') => {
    if (!leg || omioBusy || !canBook) return;
    const win = window.open('', '_blank');
    setOmioBusy(true);
    setOmioError(false);
    const clickDate = ticket.date || expectedDate || null;
    const daysUntilDeparture = clickDate
      ? (() => {
          const now = new Date();
          const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
          const ms = Date.parse(clickDate);
          return Number.isFinite(ms) ? Math.round((ms - todayUtc) / 86400000) : null;
        })()
      : null;
    // The stops ride along for the Omio lookup: always for the Omio CTA, and
    // for a night train only when it is sold on Omio (no Omio request for a
    // click headed to the operator's site).
    const nightTrainOnOmio = (draftNightTrain || leg.nightTrain)?.preferredChannel === 'omio';
    const ctx: LegBookingContext = {
      source: 'leg_sheet', daysUntilDeparture, date: clickDate,
      route: kind === 'omio' || nightTrainOnOmio ? route ?? null : null,
    };
    const request = journeyId
      ? (kind === 'omio' ? fetchLegOmioLink(journeyId, leg.id, ctx) : fetchNightTrainBookingLink(journeyId, leg.id, ctx))
      : bookingLinks![kind](leg.id, ctx);
    request
      .then(({ url }) => {
        if (win) win.location.href = url;
        else window.open(url, '_blank');
      })
      .catch(() => {
        if (win) win.close();
        setOmioError(true);
      })
      .finally(() => setOmioBusy(false));
  };
  const openOmio = () => openBookingLink('omio');
  // Night train: where it is booked (operator site with Jody's UTM, or Omio
  // when the operator is sold there).
  const openNightTrainBooking = () => openBookingLink('nightTrain');

  const currentMode = String(leg?.transportMode || 'train');
  const currentPlanned = !!leg?.planned;
  useEffect(() => {
    if (open && leg) {
      setMode(String(leg.transportMode || 'train'));
      setPlanned(!!leg.planned);
      setOverrides(leg.userOverrides || {});
      initialOverridesRef.current = overridesKey(leg.userOverrides);
      setTicket(leg.ticketDetails || {});
      initialTicketRef.current = ticketKey(leg.ticketDetails);
      setTicketEditing(null);
      setAskPlanned(false);
      setAskDiscard(false);
      setEmiHint(false);
      // Seed price bands from the leg's per-mode cache (priceEstimates): known
      // bands show instantly and are excluded from the batched LLM call.
      const seededBands: Record<string, CompareBand> = {};
      for (const [m, e] of Object.entries(leg.priceEstimates || {})) {
        if (e && Number.isFinite(Number(e.lowCents)) && Number.isFinite(Number(e.highCents))) {
          seededBands[m] = { lowCents: Number(e.lowCents), highCents: Number(e.highCents) };
        }
      }
      setPreviews(() => {
        const init: Record<string, ModePreview> = {};
        for (const [m, b] of Object.entries(seededBands)) init[m] = { band: b };
        return init;
      });
      requestedRef.current = new Set();
      legIdRef.current = leg.id;
      setCmpOpen(false);
      setCmpRows(null);
      setCmpError(false);
      setCmpPrices(seededBands);
      setCmpPricesLoading(false);
      pricesFetchedRef.current = false;

      // Deterministic facts for every mode, fetched up front (cheap, no LLM):
      // marks unavailable chips and makes every chip's facts preview instant.
      const forLeg = leg.id;
      const stillRelevant = () => legIdRef.current === forLeg;
      factsPromiseRef.current = !journeyId ? null : compareLegModes(journeyId, forLeg)
        .then((t) => {
          if (!stillRelevant()) return null;
          setCmpRows(t.modes);
          setCmpMeta({ from: t.from, to: t.to, distanceKm: t.distanceKm });
          setPreviews((p) => {
            const next = { ...p };
            for (const r of t.modes) {
              if (!r.applicable) continue;
              // Carry the airport pair too: this prefetch marks every mode as
              // "requested", so the per-mode preview below never runs and this
              // is the only chance to learn a flight's airports before saving.
              next[r.mode] = { ...next[r.mode], facts: { durationMinutes: r.durationMinutes, co2eKg: r.co2eKg }, airports: r.airports ?? null };
              requestedRef.current.add(r.mode);
            }
            return next;
          });
          return t;
        })
        .catch(() => { if (stillRelevant()) setCmpError(true); return null; });
    }
    // Re-seed the draft whenever the sheet opens for a leg.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leg?.id]);

  const openCompare = () => {
    setCmpOpen(true);
    if (leg && journeyId) {
      analytics.trackLegTransportCompared({ journey_id: journeyId, leg_id: leg.id, current_mode: currentMode });
    }
    if (pricesFetchedRef.current || !leg || !journeyId) return;
    pricesFetchedRef.current = true;
    const forLeg = leg.id;
    const stillRelevant = () => legIdRef.current === forLeg;
    // ONE batched LLM call for every applicable, non-free mode we don't
    // already have a band for (cache-seeded ones are skipped; the backend
    // also serves its cache, so repeats cost no LLM call either way). Waits
    // for the facts fetched at sheet-open to know which modes qualify.
    const have = cmpPrices;
    (factsPromiseRef.current || Promise.resolve(null)).then((t) => {
      if (!t || !stillRelevant()) return;
      const toPrice = t.modes.filter((r) => r.applicable && !r.free && !have[r.mode]).map((r) => r.mode);
      if (toPrice.length === 0) return;
      setCmpPricesLoading(true);
      compareLegPrices(journeyId, forLeg, toPrice)
        .then((res) => {
          if (!stillRelevant()) return;
          const map: Record<string, CompareBand> = {};
          for (const p of res.prices) map[p.mode] = { lowCents: p.lowCents, highCents: p.highCents };
          // Free modes have a zero band by definition.
          for (const r of t.modes) if (r.free) map[r.mode] = { lowCents: 0, highCents: 0 };
          // MERGE with the cache-seeded bands (don't replace them).
          setCmpPrices((prev) => ({ ...prev, ...map }));
          setPreviews((p) => {
            const next = { ...p };
            for (const m of Object.keys(map)) next[m] = { ...next[m], band: map[m] };
            return next;
          });
        })
        .catch(() => { /* cells fall back to n/a; chips can still fetch individually */ })
        .finally(() => { if (stillRelevant()) setCmpPricesLoading(false); });
    });
  };

  const applyCompare = (picked: string, dialogOverrides: LegUserOverrides) => {
    // Sheet draft turns dirty; its Save stays the commit point (two-level draft).
    setMode(picked);
    setOverrides(dialogOverrides);
    setCmpOpen(false);
  };

  const overridesDirty = leg != null && overridesKey(overrides) !== initialOverridesRef.current;
  const ticketDirty = leg != null && ticketKey(ticket) !== initialTicketRef.current;
  const dirty = leg != null && (mode !== currentMode || planned !== currentPlanned || overridesDirty || ticketDirty);
  const modeChanged = leg != null && mode !== currentMode;

  // Set/clear one override field for the drafted mode (Details row editors).
  const setOverrideField = (field: 'costCents' | 'durationMinutes', value: number | null) => {
    setOverrides((o) => {
      const entry = { ...(o[mode] || {}) };
      if (value == null) delete entry[field]; else entry[field] = value;
      const next = { ...o };
      if (entry.costCents == null && entry.durationMinutes == null) delete next[mode]; else next[mode] = entry;
      return next;
    });
  };

  // Live compare: fetch the drafted mode's facts (deterministic, fast) and its
  // price band (LLM, slower) the first time it's selected. Late responses for a
  // different leg are dropped via legIdRef.
  useEffect(() => {
    if (!open || !leg || !journeyId || mode === currentMode) return;
    if (requestedRef.current.has(mode)) return;
    requestedRef.current.add(mode);
    const forLeg = leg.id;
    const stillRelevant = () => legIdRef.current === forLeg;
    previewLegMode(journeyId, forLeg, mode)
      .then((f) => { if (stillRelevant()) setPreviews((p) => ({ ...p, [mode]: { ...p[mode], facts: { durationMinutes: f.durationMinutes, co2eKg: f.co2eKg }, airports: f.airports ?? null } })); })
      .catch(() => { if (stillRelevant()) setPreviews((p) => ({ ...p, [mode]: { ...p[mode], factsFailed: true } })); });
    previewLegPrice(journeyId, forLeg, mode)
      .then((b) => { if (stillRelevant()) setPreviews((p) => ({ ...p, [mode]: { ...p[mode], band: b } })); })
      .catch(() => { if (stillRelevant()) setPreviews((p) => ({ ...p, [mode]: { ...p[mode], bandFailed: true } })); });
  }, [open, leg, journeyId, mode, currentMode]);

  const performSave = async (markPlanned: boolean) => {
    if (!leg) return;
    setSaving(true);
    if (markPlanned) setPlanned(true);
    const effectivePlanned = markPlanned ? true : planned;
    const changes: { transportMode?: string; planned?: boolean; plannedVia?: 'sheet' | 'save_dialog'; userOverrides?: LegUserOverrides | null; ticketDetails?: LegTicketDetails | null } = {};
    if (mode !== currentMode) changes.transportMode = mode;
    if (effectivePlanned !== currentPlanned) {
      changes.planned = effectivePlanned;
      // Analytics context (leg_planned_toggled.via): did the toggle come from
      // the sheet's pill or the "Mark planned and save" dialog?
      changes.plannedVia = markPlanned ? 'save_dialog' : 'sheet';
    }
    if (overridesDirty) changes.userOverrides = Object.keys(overrides).length > 0 ? overrides : null;
    if (ticketDirty) {
      const extra = (ticket.extraInfos || '').trim();
      // A time without a date is meaningless, so it never persists: the UI
      // keeps the time input locked until a date exists, and this drops any
      // stray time (e.g. legacy data) rather than saving an orphan.
      const cleanDate = ticket.date || null;
      const cleaned: LegTicketDetails = { date: cleanDate, time: cleanDate ? ticket.time || null : null, extraInfos: extra || null };
      changes.ticketDetails = cleaned.date || cleaned.time || cleaned.extraInfos ? cleaned : null;
    }
    const ok = await onSave(leg.id, changes);
    setSaving(false);
    if (ok) onClose();
  };

  // Close attempts go through here: with unsaved draft edits, confirm first
  // (accidental backdrop taps were silently losing typed data — Paulo
  // 2026-07-18). Saving already closes via onClose directly.
  const requestClose = () => {
    if (saving) return;
    if (dirty) setAskDiscard(true);
    else onClose();
  };

  const save = () => {
    if (!leg || !dirty) return;
    // Saving a newly set/changed date while the leg is still Unplanned: offer
    // to mark it planned in the same save (Paulo 2026-07-18). Never blocks —
    // both dialog choices proceed with the save.
    const dateChanged = (ticket.date || null) !== (leg.ticketDetails?.date || null);
    if (dateChanged && ticket.date && !planned) {
      setAskPlanned(true);
      return;
    }
    void performSave(false);
  };

  // Displayed facts: the saved leg's server values for the current mode, the
  // live preview for a drafted one.
  const serverMin = (() => {
    const m = coerceDecimal(leg?.durationMinutes, NaN);
    if (Number.isFinite(m)) return m;
    const h = coerceDecimal(leg?.durationHours, NaN);
    return Number.isFinite(h) ? h * 60 : NaN;
  })();
  const serverKg = coerceDecimal(leg?.co2eKg, NaN);
  const serverLowC = coerceDecimal(leg?.priceBandLowCents, NaN);
  const serverHighC = coerceDecimal(leg?.priceBandHighCents, NaN);
  const serverPriced = Number.isFinite(serverLowC) && Number.isFinite(serverHighC);

  const pv = previews[mode];
  // Estimates for the drafted mode (server values for the current mode, live
  // preview for a drafted one) — these keep flowing even while overridden, so
  // "Use estimate" always knows what it goes back to.
  const estMin = modeChanged ? coerceDecimal(pv?.facts?.durationMinutes, NaN) : serverMin;
  const estKg = modeChanged ? coerceDecimal(pv?.facts?.co2eKg, NaN) : serverKg;
  // The night train of the DRAFTED mode: the leg's own when it is already a
  // night train, else the one the compare table found for this leg.
  const draftNightTrain = !isNightTrainMode(mode)
    ? null
    : ((mode === currentMode ? leg?.nightTrain : null) ?? cmpRows?.find((r) => isNightTrainMode(r.mode))?.nightTrain ?? null);
  // A night train's time is its timetable ride: exact, not a rounded estimate.
  const estTimeStr = Number.isFinite(estMin)
    ? (draftNightTrain ? fmtRide(estMin) : formatDurationMin(estMin))
    : (modeChanged && !pv?.factsFailed ? '…' : '—');
  const estPriceStr = modeChanged
    ? (pv?.band ? bandStr(pv.band.lowCents, pv.band.highCents) : (pv?.bandFailed ? '—' : 'estimating…'))
    : (serverPriced ? bandStr(serverLowC, serverHighC) : 'estimating…');
  const estPriceKnown = modeChanged ? !!pv?.band : serverPriced;

  // User override for the DRAFTED mode wins over the estimate.
  const ov = overrides[mode];
  const timeOverridden = ov?.durationMinutes != null;
  const priceOverridden = ov?.costCents != null;
  const dispMin = timeOverridden ? Number(ov!.durationMinutes) : estMin;
  // CO2e is never edited directly, but a user TIME implies a refined distance,
  // so the kg scale with it (kg and duration are both linear in distance; the
  // facts row keeps duration and kg on the same scale, so the ratio is exact).
  const rowMin = coerceDecimal(pv?.facts?.durationMinutes, NaN);
  const rowKg = coerceDecimal(pv?.facts?.co2eKg, NaN);
  const kgSrc = Number.isFinite(rowKg) ? rowKg : estKg;
  const minSrc = Number.isFinite(rowMin) ? rowMin : estMin;
  const dispKg = timeOverridden && Number.isFinite(kgSrc) && Number.isFinite(minSrc) && minSrc > 0
    ? kgSrc * (Number(ov!.durationMinutes) / minSrc)
    : estKg;
  const timeStr = timeOverridden ? formatDurationExact(Number(ov!.durationMinutes)) : estTimeStr;
  const priceStr = priceOverridden ? formatEuroExact(Number(ov!.costCents)) : estPriceStr;
  const priceKnown = priceOverridden || estPriceKnown;

  const emiLevel = getEmissionLevel(mode);
  const emiColor = EMISSION_LEVEL_COLOR[emiLevel];
  const emiLabel = EMISSION_LEVEL_LABEL[emiLevel];

  // Airport pair, flight legs only. The backend sends `airports` for those
  // alone; guard on the CURRENT drafted mode too, so switching the sheet's
  // mode chip to a ground option hides the row immediately instead of leaving
  // airports beside a train.
  // Prefer the PREVIEWED mode's pair (arrives when the user taps a mode chip)
  // over the persisted leg's, so switching train → airplane fills the row
  // immediately instead of only after saving and reopening the sheet.
  const legAirports = mode !== 'airplane'
    ? null
    : (previews[mode]?.airports ?? (mode === currentMode ? leg?.airports || null : null));

  const summaryBits = [getModeLabel(mode)];
  if (Number.isFinite(dispMin)) summaryBits.push(timeStr);
  if (priceKnown) summaryBits.push(priceStr);

  // Modes the facts table marked not applicable can't be picked from the chips
  // either (same rules as the compare dialog). The drafted mode stays
  // interactive so the user is never stranded on a disabled chip; modes not in
  // the table (e.g. 'auto') are left alone.
  const isUnavailable = (m: string): boolean => {
    if (!cmpRows || m === mode) return false;
    const r = cmpRows.find((x) => x.mode === m);
    return r ? !r.applicable : false;
  };

  // "Night train" is always in the list, right under Train (spec §7.3);
  // where the leg cannot take one it is disabled and says why. The compare
  // table answers once loaded; before that, the leg itself. Other modes this
  // leg cannot use stay in the list too, greyed, like the chips they replace.
  const nightTrainRow = cmpRows?.find((r) => isNightTrainMode(r.mode));
  const nightLeg = isNightTrainMode(mode) || isNightTrainMode(currentMode);
  const nightState = nightTrainRow
    ? { status: nightTrainRow.nightTrainStatus, note: nightTrainRow.nightTrainNote, available: nightTrainRow.applicable, current: nightLeg, search: leg?.nightTrainSearch }
    : { status: leg?.nightTrainStatus, note: leg?.nightTrainNote, available: leg?.nightTrainAvailable, current: nightLeg, search: leg?.nightTrainSearch };
  // The search is offered where the two places are far enough apart for a
  // night train (a night-train leg always is).
  const openSearch = leg && onSearchNightTrainsNearby && (nightLeg || canSearchNearby(nightState.available ? 'available' : nightState.status))
    ? () => onSearchNightTrainsNearby(leg.id)
    : undefined;
  // Where it would find nothing (the backend looked when it read the
  // journey), the sheet says so in its place. Counted without the train the
  // leg is saved with, so "other" goes by the saved mode.
  const searchBlocked = openSearch ? searchBlockedReason(leg?.nightTrainSearch, isNightTrainMode(currentMode)) : null;
  // "Night train" says where the leg stands: a direct one to pick, or none
  // and the search in its place. Where there is a night train to pick or in
  // use, the search is at the foot of the list.
  const modeOptions = modeOptionsForLeg(
    TRANSPORT_MODE_OPTIONS.map((o) => ({ ...o, disabled: isUnavailable(o.value) })),
    nightState,
    openSearch,
  );
  const searchNearby = openSearch && !searchBlocked && (nightLeg || nightState.available)
    ? { label: <><span aria-hidden>🌙 </span>{nightLeg ? NEARBY_SEARCH_OTHER_LABEL : NEARBY_SEARCH_LABEL}</>, onSelect: openSearch }
    : undefined;

  return (
    <>
    <BottomSheet
      open={open}
      onClose={requestClose}
      ariaLabel={`Travel details: ${from} to ${to}`}
      actions={dirty ? (
        <>
          <button
            type="button"
            onClick={requestClose}
            disabled={saving}
            style={{ flex: 'none', background: '#f4f6f3', color: '#6c7872', border: '1px solid rgba(20,40,30,0.06)', borderRadius: 12, padding: '12px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Discard
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || busy}
            style={{ flex: 1, background: '#157a55', color: '#fff', border: 'none', borderRadius: 12, padding: 12, fontSize: 14, fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      ) : undefined}
    >
      {/* Hero */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '6px 36px 2px 2px' }}>
        <span aria-hidden style={{ fontSize: 34, lineHeight: 1, flex: 'none' }}>{getTransportEmoji(mode) || '🚏'}</span>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: 25, margin: 0, color: '#16241d' }}>{from} → {to}</h2>
          <div style={{ fontSize: 13, color: '#6c7872', marginTop: 2 }}>{summaryBits.join(' · ')}</div>
        </div>
      </div>

      {/* How you'll travel */}
      <div style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 8, marginBottom: 11 }}>
          <h3 style={sheetH}>How you'll travel</h3>
          {/* Alternatives leads as the primary (Paulo 2026-08-05): comparing
              before booking is the brand promise, and a solid green "buy"
              button first would read as salesy. The Omio CTA moved into the
              always-open "Your ticket" section (Paulo 2026-08-24). */}
          <button
            type="button"
            className="cmp-cta"
            onClick={openCompare}
            disabled={saving}
            style={{ ...ctaPrimary, marginLeft: 10, opacity: saving ? 0.6 : 1 }}
          >
            {compactLabel ? 'Alternatives' : 'Alternatives overview'}
          </button>
          {/* Night trains for this part of the trip, next to the other way
              of looking at alternatives (Paulo 2026-09-29). */}
          {openSearch && (
            <span style={{ marginLeft: 8, display: 'inline-flex', minWidth: 0 }}>
              <NightTrainSearchButton
                label={isNightTrainMode(currentMode) ? NEARBY_SEARCH_OTHER_LABEL : NEARBY_SEARCH_LABEL}
                disabled={saving}
                blocked={searchBlocked}
                onClick={openSearch}
              />
            </span>
          )}
        </div>
        {/* The shared dropdown (docs/codebase/Dropdown.md), full width under
            the header, replacing the mode chips and their ellipsis (Paulo
            2026-09-28). Portalled: the sheet's body scrolls and would clip
            the menu. */}
        <Dropdown
          portal
          ariaLabel={`Transport for ${from} to ${to}`}
          options={modeOptions}
          footer={searchNearby}
          value={mode}
          onChange={setMode}
          disabled={saving}
        />
      </div>

      {/* Booking button: always in view, never inside a section (Paulo
          2026-09-28), on legs whose ticket is not booked yet. A night train
          goes to where it is sold (spec §7.2); other modes Omio sells get the
          Omio affiliate CTA (docs/integrations/omio-impact.md). The
          commission disclosure sits at the click ("never changes your
          price" earns the trust, Paulo 2026-07-30). */}
      {!planned && canBook && (draftNightTrain || OMIO_MODES.has(mode)) && (
        <div style={{ marginTop: 14 }}>
          <button
            type="button"
            data-share-ok
            onClick={draftNightTrain ? openNightTrainBooking : openOmio}
            disabled={omioBusy}
            style={{ ...ctaSecondary, width: '100%', justifyContent: 'center', cursor: omioBusy ? 'default' : 'pointer', opacity: omioBusy ? 0.7 : 1 }}
          >
            {omioBusy
              ? <>Opening <Dots /></>
              : <>{draftNightTrain ? bookingLabel(draftNightTrain, 'sheet') : 'Find tickets on Omio'}<ExternalGlyph /></>}
          </button>
          <div style={{ fontSize: 12, color: '#6c7872', marginTop: 8 }}>
            {draftNightTrain && draftNightTrain.preferredChannel !== 'omio'
              ? <>Prices are set by {draftNightTrain.operatorName || draftNightTrain.productName}. Jody earns nothing from this link.</>
              : <>Jody may earn a commission when you book through this link. <strong style={{ color: '#16241d', fontWeight: 700 }}>This never changes your price.</strong></>}
          </div>
          {omioError && (
            <div style={{ fontSize: 12, color: '#b3564a', marginTop: 8 }}>
              Could not open the ticket search. Please try again.
            </div>
          )}
        </div>
      )}

      {/* Night train (spec §7.2): the train, stations, beds and run dates. */}
      {draftNightTrain && (
        <NightTrainSheetBlock
          train={draftNightTrain}
          fromCity={from}
          toCity={to}
          open={openSections.nightTrain}
          onToggle={() => toggleSection('nightTrain')}
          changeAtFrom={changeAtFrom}
          changeAtTo={changeAtTo}
          shiftBusy={shiftBusy || saving}
          onShiftDate={onShiftNightTrainDate && leg && mode === currentMode
            ? (date) => {
              setShiftBusy(true);
              onShiftNightTrainDate(leg.id, date).finally(() => setShiftBusy(false));
            }
            : undefined}
        />
      )}

      {/* Your ticket: the booked switch and the ticket's date, price and
          notes. Collapsible like the rest (Paulo 2026-09-28; it was always
          open from 2026-08-24 for the sake of the booking button, which now
          has its own place above). The pill is a status display, in view
          even when the section is closed; the Switch row is the control. */}
      <div style={{ marginTop: 20 }}>
        <SectionToggle title="Your ticket" open={openSections.ticket} onToggle={() => toggleSection('ticket')}>
          <Tooltip content={planned ? 'Booked: your ticket for this leg is sorted.' : 'Not booked yet. Open this section and use the toggle once your ticket is sorted.'} widthClassName="w-max">
            <span style={{ display: 'inline-flex' }}>
              <PlannedPill planned={planned} disabled={saving} />
            </span>
          </Tooltip>
        </SectionToggle>
        {/* Ticket details: date (estimate = the schedule's expected day; a
            user-set date re-anchors the timeline), departure time, extra infos.
            Same estimate-vs-user-value language as the Details rows. */}
        {openSections.ticket && (
        <div style={{ border: '1px solid rgba(20,40,30,0.06)', borderRadius: 16, overflow: 'hidden' }}>
          {/* The booked status control (Paulo 2026-08-24): a labeled Switch
              row, first in the section; the header pill only displays it.
              Draft state like every other field; commits on Save. */}
          <div style={fieldRow}>
            <span style={rowLab}>Ticket booked</span>
            <Switch on={planned} onToggle={() => setPlanned(!planned)} ariaLabel="Ticket booked" disabled={saving} />
          </div>
          {/* Date and time in ONE row (Paulo 2026-07-30). A time without a
              date is meaningless, so the time input only unlocks once a date
              exists, and clearing the date clears the time with it. */}
          <div style={fieldRow}>
            <span style={rowLab}>Date and time {!ticket.date && expectedDate && <span aria-hidden style={{ color: '#aab4af' }}>*</span>}</span>
            {ticketEditing === 'datetime' ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {ticket.date && (
                  <button
                    type="button"
                    onClick={() => { setTicket((t) => ({ ...t, date: null, time: null })); setTicketEditing(null); }}
                    style={{ background: 'transparent', border: 'none', color: '#157a55', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                  >
                    {expectedDate ? `Use expected date (${fmtIsoDay(expectedDate)})` : 'Clear'}
                  </button>
                )}
                <input
                  type="date"
                  autoFocus
                  defaultValue={ticket.date || expectedDate || ''}
                  disabled={saving}
                  aria-label="travel date"
                  onChange={(e) => setTicket((t) => ({ ...t, date: e.target.value || null, time: e.target.value ? t.time : null }))}
                  style={{ font: 'inherit', fontSize: 14, fontWeight: 600, color: '#16241d', border: '1px solid rgba(20,40,30,0.14)', borderRadius: 8, padding: '4px 8px' }}
                />
                <input
                  type="time"
                  defaultValue={ticket.time || ''}
                  disabled={saving || !ticket.date}
                  aria-label="departure time"
                  title={ticket.date ? undefined : 'Set a date first'}
                  onChange={(e) => setTicket((t) => ({ ...t, time: e.target.value || null }))}
                  onBlur={() => setTicketEditing(null)}
                  style={{ font: 'inherit', fontSize: 14, fontWeight: 600, color: '#16241d', border: '1px solid rgba(20,40,30,0.14)', borderRadius: 8, padding: '4px 8px', opacity: ticket.date ? 1 : 0.5 }}
                />
              </span>
            ) : (
              <button type="button" onClick={() => setTicketEditing('datetime')} disabled={saving} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>
                {ticket.date ? (
                  <span style={{ ...rowVal, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {fmtIsoDay(ticket.date)}
                    {ticket.time
                      ? <span>, {ticket.time}</span>
                      : <span style={{ fontWeight: 500, color: '#8a948f' }}>· add time</span>}
                    <span style={{ color: '#157a55', display: 'inline-flex' }}><PencilGlyph /></span>
                  </span>
                ) : expectedDate ? (
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#16241d', borderBottom: '1px dashed rgba(20,40,30,0.35)' }}>
                    {fmtIsoDay(expectedDate)}
                  </span>
                ) : (
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#8a948f' }}>Add date</span>
                )}
              </button>
            )}
          </div>
          <div style={fieldRow}>
            <span style={rowLab}>Ticket price {!priceOverridden && <span aria-hidden style={{ color: '#aab4af' }}>*</span>}</span>
            <EditableRowValue
              display={priceStr}
              overridden={priceOverridden}
              estimateLabel={estPriceStr}
              placeholder="e.g. 39.50"
              ariaLabel="ticket price"
              invalidHint="Enter a price like 39.50."
              disabled={saving}
              muted={!priceKnown}
              onCommit={(raw) => {
                const cents = parseEuroInput(raw);
                if (cents == null) return false;
                setOverrideField('costCents', cents);
                return true;
              }}
              onReset={() => setOverrideField('costCents', null)}
            />
          </div>
          <div style={{ ...fieldRow, borderBottom: 'none' }}>
            <span style={rowLab}>Extra infos</span>
            {ticketEditing === 'extra' ? (
              <input
                type="text"
                autoFocus
                maxLength={500}
                defaultValue={ticket.extraInfos || ''}
                placeholder="Seat, coach, booking reference…"
                disabled={saving}
                onChange={(e) => setTicket((t) => ({ ...t, extraInfos: e.target.value }))}
                onBlur={() => setTicketEditing(null)}
                onKeyDown={(e) => { if (e.key === 'Enter') setTicketEditing(null); }}
                style={{ font: 'inherit', fontSize: 14, fontWeight: 500, color: '#16241d', border: '1px solid rgba(20,40,30,0.14)', borderRadius: 8, padding: '4px 8px', flex: 1, minWidth: 0 }}
              />
            ) : (
              <button type="button" onClick={() => setTicketEditing('extra')} disabled={saving} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', minWidth: 0 }}>
                {(ticket.extraInfos || '').trim() ? (
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#16241d', display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 220 }} title="Your notes. Tap to change them.">
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(ticket.extraInfos || '').trim()}</span>
                    <span style={{ color: '#157a55', display: 'inline-flex', flex: 'none' }}><PencilGlyph /></span>
                  </span>
                ) : (
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#8a948f' }}>Add notes</span>
                )}
              </button>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Details (live: previews the drafted mode; time + price are tap-to-edit
          per-mode overrides, emissions read-only with a tap hint). Sits AFTER
          Your ticket since 2026-08-24: booking outranks reference data. */}
      <div style={{ marginTop: 20 }}>
        <SectionToggle title="Details" open={openSections.details} onToggle={() => toggleSection('details')} />
        {openSections.details && (<>
        <div style={{ border: '1px solid rgba(20,40,30,0.06)', borderRadius: 16, overflow: 'hidden' }}>
          <div style={fieldRow}>
            <span style={rowLab}>
              Travel time {!timeOverridden && !draftNightTrain && <span aria-hidden style={{ color: '#aab4af' }}>*</span>}
              {/* Flights carry a fixed 2 h 30 of ground time. Saying so where the
                  number lives stops it reading as an error, and it is the same
                  story the Airports row below tells. */}
              {legAirports && !timeOverridden && (
                <Tooltip content={FLIGHT_TIME_TOOLTIP} widthClassName="w-64">
                  <span style={{ marginLeft: 5, color: '#aab4af', cursor: 'help', fontWeight: 700 }} aria-label="Why is this longer than the flight?">ⓘ</span>
                </Tooltip>
              )}
            </span>
            <EditableRowValue
              display={timeStr}
              overridden={timeOverridden}
              estimateLabel={estTimeStr}
              placeholder="e.g. 5:30"
              ariaLabel="travel time"
              invalidHint="Enter a time like 5:30 or 90m."
              disabled={saving}
              onCommit={(raw) => {
                const min = parseDurationInput(raw);
                if (min == null || min <= 0) return false;
                setOverrideField('durationMinutes', min);
                return true;
              }}
              onReset={() => setOverrideField('durationMinutes', null)}
            />
          </div>
          <div
            style={{ ...fieldRow, borderBottom: 'none', cursor: 'default' }}
            onClick={showEmiHint}
          >
            <span style={rowLab}>Emissions</span>
            {/* An unknown/auto mode shows no emissions (a defaulted "Low" would mislead). */}
            {isKnownTransportMode(mode) ? (
              <span style={rowVal}>
                <span style={{ color: emiColor }}>{emiLabel}</span>
                {Number.isFinite(dispKg) && <span style={{ fontWeight: 500, color: '#6c7872' }}> ({Math.round(dispKg)}kg CO₂e)</span>}
              </span>
            ) : (
              <span style={{ ...rowVal, color: '#6c7872', fontWeight: 500 }}>—</span>
            )}
          </div>
          {emiHint && (
            <div style={{ padding: '0 14px 10px', fontSize: 11.5, color: '#6c7872' }}>
              {isNightTrainMode(mode) ? `${NIGHT_TRAIN_EMISSIONS_NOTE} ` : ''}Calculated from distance and transport mode, so it can't be edited directly.
            </div>
          )}
        </div>
        <div style={{ fontSize: 10.5, color: '#aab4af', marginTop: 6 }}>* estimated · tap a value to enter your own</div>
        </>)}
      </div>

      {/* Which airports this flight could use. Sits in TICKETS, not Details:
          "which airport am I flying from" is a booking-time question, and it
          belongs beside the Omio CTA you act on. A LIST per city because no
          dataset field separates a commercial hub from a private-aviation
          field, so the nearest is often the wrong one (Paris → Le Bourget
          over Charles de Gaulle). Nearest first; the rest collapse. */}
      {legAirports && (
        <div style={{ marginTop: 20 }}>
          <SectionToggle title="Airports" open={openSections.airports} onToggle={() => toggleSection('airports')} />
          {openSections.airports && (<>
          <div style={{ border: '1px solid rgba(20,40,30,0.06)', borderRadius: 16, overflow: 'hidden' }}>
            {[legAirports.from, legAirports.to].filter((c) => c && c.airports && c.airports.length).map((cityGroup, gi, shown) => {
              const expanded = !!airportsExpanded[cityGroup.city];
              const list = expanded ? cityGroup.airports : cityGroup.airports.slice(0, AIRPORTS_SHOWN);
              const hidden = cityGroup.airports.length - list.length;
              return (
                <div key={cityGroup.city + gi} style={{ padding: '11px 14px', borderBottom: gi === shown.length - 1 ? 'none' : '1px solid rgba(20,40,30,0.06)' }}>
                  {/* City name and the values-column header share one line, and
                      the header repeats per city (origin AND destination) so
                      each block is readable on its own (Paulo 2026-07-30).
                      Shortened on narrow screens via the compactLabel rule. */}
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                    <span style={{ ...rowLab, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cityGroup.city}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 600, color: '#aab4af', whiteSpace: 'nowrap', flex: 'none' }}>
                      {compactLabel ? 'From centre' : 'Distance from city centre'}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gap: 5 }}>
                    {list.map((ap) => (
                      <div key={ap.iata || ap.name} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                        {/* No tooltip: the code and name are already on
                            screen, so the official name adds nothing.
                            minWidth 0 + wrapping so a long official name
                            ("Josep Tarradellas Barcelona-El Prat") wraps on a
                            phone instead of pushing the value off-screen. */}
                        <span style={{
                          fontSize: 13.5, color: '#16241d', minWidth: 0, overflowWrap: 'anywhere',
                          // Clamp to two lines: a very long official name
                          // ("Josep Tarradellas Barcelona-El Prat") wrapped to
                          // three lines on a 375px screen and made the rows
                          // ragged. The IATA code always survives, since it
                          // leads and is what a booking is matched against.
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                          <span style={{ fontWeight: 700 }}>{ap.iata || ap.name}</span>
                          {ap.iata && <span style={{ fontWeight: 500, color: '#6c7872' }}> {ap.name}</span>}
                        </span>
                        <span style={{ fontSize: 11.5, color: '#aab4af', whiteSpace: 'nowrap', flex: 'none' }}>
                          {ap.transferMinutes ? `${ap.transferMinutes} min (${ap.distanceFromCityKm} km)` : `${ap.distanceFromCityKm} km`}
                        </span>
                      </div>
                    ))}
                  </div>
                  {(hidden > 0 || expanded) && (
                    <button
                      type="button"
                      onClick={() => setAirportsExpanded((e) => ({ ...e, [cityGroup.city]: !expanded }))}
                      style={{ marginTop: 6, font: 'inherit', fontSize: 11.5, fontWeight: 600, color: '#157a55', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    >
                      {expanded ? 'Show fewer' : `+${hidden} more`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 10.5, color: '#aab4af', marginTop: 6 }}>Time/distances are the approximate commute to the airport by road.</div>
          </>)}
        </div>
      )}

    </BottomSheet>
    <CompareTransportDialog
      open={cmpOpen}
      from={cmpMeta.from || from}
      to={cmpMeta.to || to}
      distanceKm={cmpMeta.distanceKm}
      rows={cmpRows}
      error={cmpError}
      prices={cmpPrices}
      pricesLoading={cmpPricesLoading}
      initialMode={mode}
      initialOverrides={overrides}
      onSave={applyCompare}
      onClose={() => setCmpOpen(false)}
    />
    {/* Save rescue path on the dialog (Paulo 2026-08-18), same as the stop
        sheet's: rescuing edits is one tap instead of cancel-then-save. */}
    {askDiscard && (
      <ConfirmationDialog
        title="Discard unsaved changes?"
        message="You have edits on this leg that were not saved yet. Closing now will lose them."
        confirmText="Discard changes"
        cancelText="Keep editing"
        confirmButtonStyle="danger"
        onConfirm={() => { setAskDiscard(false); onClose(); }}
        onCancel={() => setAskDiscard(false)}
        extraText="Save"
        onExtra={() => { setAskDiscard(false); save(); }}
      />
    )}
    {askPlanned && (
      <ConfirmationDialog
        title="Ticket booked?"
        message="You set a date for this leg. Is your ticket booked already?"
        confirmText="Mark booked and save"
        cancelText="Save as not booked"
        confirmButtonStyle="primary"
        onConfirm={() => { setAskPlanned(false); void performSave(true); }}
        onCancel={() => { setAskPlanned(false); void performSave(false); }}
      />
    )}
    </>
  );
};

export default LegSheet;
